<template>
  <div v-on-clickaway="closeSelector" class="relative">
    <div
      class="flex items-center justify-end shrink-0 w-12"
      style="
        background: linear-gradient(
          -90deg,
          #f5f5f5 50%,
          rgba(245, 245, 245, 0) 100%
        );
      "
      @click="toggleSelector"
    >
      <button
        class="flex items-center justify-center text-gray-600 hover:text-gray-800 cursor-pointer outline-none border-0 bg-transparent"
        @click="$emit('more-actions')"
      >
        <span class="material-symbols-outlined text-[24px] leading-none">
          more_horiz
        </span>
      </button>
    </div>
    <dropdown-panel
      v-if="isOpenSelector"
      :items="actionItems"
      :direction="isRTLView ? 'left-0' : 'right-0'"
      @select="onSelectOption"
    >
      <template #item="{ item }">
        <span class="my-[3px]">{{ item.name }}</span>
      </template>
    </dropdown-panel>
    <email-transcript-modal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailActionsModal"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import conversationMixin from 'dashboard/mixins/conversations';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import rtlMixin from 'shared/mixins/rtlMixin';
import alertMixin from 'shared/mixins/alertMixin';
import DropdownPanel from './DropdownPanel.vue';
import EmailTranscriptModal from 'dashboard/components/widgets/conversation/EmailTranscriptModal.vue';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { downloadEmlFile } from 'dashboard/helper/downloadHelper';
import { format, fromUnixTime } from 'date-fns';
import * as XLSX from 'xlsx';

export default {
  components: { DropdownPanel, EmailTranscriptModal },
  mixins: [
    clickaway,
    conversationMixin,
    hasPermissionMixin,
    rtlMixin,
    alertMixin,
  ],
  data() {
    return {
      isOpenSelector: false,
      showEmailActionsModal: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    actionItems() {
      return [
        {
          action: this.toggleEmailActionsModal,
          disabled: !this.hasInboxPermission('Inbox send Transcript'),
          name: this.$t('CONTACT_PANEL.SEND_TRANSCRIPT'),
        },
        {
          action: this.setUnread,
          disabled: this.unreadCount > 0 || this.incomingCount === 0,
          name: this.$t('CONVERSATION.HEADER.MARK_AS_UNREAD'),
        },
        {
          action: this.downloadConversation,
          disabled: false,
          name: this.$t('CONTACT_PANEL.EXPORT_CONVERSATION'),
        },
        ...(this.canDownloadEmail
          ? [
              {
                action: this.downloadEmail,
                disabled: false,
                name: this.$t('CONTACT_PANEL.DOWNLOAD_EMAIL'),
              },
            ]
          : []),
      ];
    },
    canDownloadEmail() {
      const inbox = this.$store.getters['inboxes/getInbox'](
        this.currentChat?.inbox_id
      );
      return (
        inbox?.provider === 'microsoft_api' &&
        this.hasInboxPermission('Inbox download email')
      );
    },
    unreadCount() {
      return this.unreadMessagesCount(this.currentChat);
    },
    incomingCount() {
      return this.incomingMessages(this.currentChat).length;
    },
    currentContact() {
      return this.$store.getters['contacts/getContact'](
        this.currentChat.meta.sender.id
      );
    },
  },
  methods: {
    toggleSelector() {
      this.isOpenSelector = !this.isOpenSelector;
    },
    closeSelector() {
      this.isOpenSelector = false;
    },
    onSelectOption(option) {
      if (option.disabled) return;

      option.action();
    },
    setUnread() {
      this.$store.dispatch('markConversationUnread', this.currentChat);
    },
    toggleEmailActionsModal() {
      this.showEmailActionsModal = !this.showEmailActionsModal;
      this.closeSelector();
    },
    downloadConversation() {
      this.closeSelector();
      const data = this.prepareExportData();

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Conversation');

      // Generate filename
      const time = format(new Date(), 'dd.MM.yyyy_HH.mm');
      const filename = `conversation-${time}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
    },
    async downloadEmail() {
      this.closeSelector();
      try {
        const { data } = await ConversationApi.downloadEmail(
          this.currentChat.id
        );
        downloadEmlFile(`conversation-${this.currentChat.id}.eml`, data);
      } catch (error) {
        this.showAlert(this.$t('CONTACT_PANEL.DOWNLOAD_EMAIL_ERROR'));
      }
    },
    prepareExportData() {
      const exportData = [];
      this.currentChat.messages.filter((item, index) => {
        if (item.sender) {
          exportData.push({
            id: index,
            contact: this.currentContact.phone_number,
            date: this.messageStamp(item.created_at, 'dd.MM.yyyy'),
            time: this.messageStamp(item.created_at, 'h:mm a'),
            from: item.message_type === 0 ? 'client' : 'agent',
            from_name:
              item.message_type === 0
                ? this.currentContact.name
                : item.sender.name,
            to: item.message_type === 0 ? 'agent' : 'client',
            to_name: item.message_type === 1 ? this.currentContact.name : '',
            message: this.getMessage(item),
          });
        }
        return item;
      });
      return exportData;
    },
    messageStamp(time, dateFormat = 'h:mm a') {
      const unixTime = fromUnixTime(time);
      return format(unixTime, dateFormat);
    },
    getMessage(item) {
      if (item.attachments && item.attachments.length > 0) {
        return item.attachments[0].data_url;
      }
      return item.content;
    },
  },
};
</script>
